import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../components/layout/AdminSidebar';
import {
  AlertTriangle,
  Bell,
  Send,
  CheckCircle,
  XCircle,
  Plus,
  Users,
  MapPin,
  Clock,
  ArrowLeft,
  Loader2,
  Filter,
  RotateCcw,
  Smartphone,
  Mail,
  MessageSquare,
  Radio
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api/v1';

const ALERT_TYPE_ICONS = {
  fire: '🔥',
  weather: '⛈️',
  security: '🚨',
  infrastructure: '🔧',
  wildlife: '🐻',
  road_closure: '🚧',
  utility_outage: '⚡',
  evacuation: '🚪',
  medical: '🏥',
  general: '📢'
};

const PRIORITY_COLORS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white'
};

const STATUS_COLORS = {
  draft: 'bg-gray-500',
  active: 'bg-red-500 animate-pulse',
  resolved: 'bg-green-500',
  cancelled: 'bg-gray-400'
};

const CHANNEL_ICONS = {
  sms: Smartphone,
  email: Mail,
  push: Bell,
  in_app: MessageSquare
};

const EmergencyAlertsPage = ({ embedded = false }) => {
  const [alerts, setAlerts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [sending, setSending] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [newAlert, setNewAlert] = useState({
    title: '',
    message: '',
    emergency_type: 'general',
    priority: 'medium',
    target_audience: 'all',
    channels: ['push', 'email', 'in_app'],
    action_required: '',
    affected_area: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [alertsRes, templatesRes, configRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/emergency/alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/emergency/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/emergency/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/emergency/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAlerts(alertsRes.data || []);
      setTemplates(templatesRes.data.templates || []);
      setConfig(configRes.data);
      setDashboardStats(dashboardRes.data);
    } catch (error) {
      console.error('Failed to fetch emergency data:', error);
      toast.error('Failed to load emergency alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!newAlert.title || !newAlert.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      const response = await axios.post(`${API}/emergency/alerts`, newAlert, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAlerts([response.data.alert, ...alerts]);
      setShowCreateDialog(false);
      setNewAlert({
        title: '',
        message: '',
        emergency_type: 'general',
        priority: 'medium',
        target_audience: 'all',
        channels: ['push', 'email', 'in_app'],
        action_required: '',
        affected_area: ''
      });
      toast.success('Alert created successfully');
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  const handleSendAlert = async (alertId) => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/emergency/alerts/${alertId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Alert sent to ${response.data.recipients} recipients`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send alert');
    } finally {
      setSending(false);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await axios.post(`${API}/emergency/alerts/${alertId}/resolve`, 
        { resolution_notes: 'Situation resolved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Alert resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const applyTemplate = (template) => {
    setNewAlert({
      ...newAlert,
      title: template.title,
      message: template.message,
      emergency_type: template.emergency_type,
      priority: template.priority,
      channels: template.channels,
      action_required: template.action_required || ''
    });
    setShowTemplateDialog(false);
    setShowCreateDialog(true);
  };

  const toggleChannel = (channel) => {
    const current = newAlert.channels || [];
    if (current.includes(channel)) {
      setNewAlert({ ...newAlert, channels: current.filter(c => c !== channel) });
    } else {
      setNewAlert({ ...newAlert, channels: [...current, channel] });
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (activeTab === 'active') return alert.status === 'active';
    if (activeTab === 'draft') return alert.status === 'draft';
    if (activeTab === 'resolved') return alert.status === 'resolved';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  const pageContent = (
    <div className="p-6 lg:p-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white px-6 py-6 rounded-xl mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Emergency Alerts</h1>
                <p className="text-red-200">Crisis Communication Center</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowTemplateDialog(true)}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Radio className="w-4 h-4 mr-2" />
                Quick Alert
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-white text-red-700 hover:bg-red-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Dashboard Stats */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className={dashboardStats.active_alerts > 0 ? 'border-red-500 bg-red-50' : ''}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`p-3 rounded-lg ${dashboardStats.active_alerts > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-100'}`}>
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardStats.active_alerts}</p>
                  <p className="text-sm text-gray-600">Active Alerts</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-100">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardStats.resolved_today}</p>
                  <p className="text-sm text-gray-600">Resolved Today</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-100">
                  <Bell className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardStats.total_this_month}</p>
                  <p className="text-sm text-gray-600">This Month</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium text-gray-600 mb-2">Emergency Contacts</p>
                <div className="space-y-1">
                  {config?.emergency_contacts?.slice(0, 2).map((contact, i) => (
                    <p key={i} className="text-xs">
                      <span className="font-medium">{contact.name}:</span> {contact.phone}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${alerts.filter(a => a.status === 'active').length > 0 ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`} />
              Active ({alerts.filter(a => a.status === 'active').length})
            </TabsTrigger>
            <TabsTrigger value="draft">
              Draft ({alerts.filter(a => a.status === 'draft').length})
            </TabsTrigger>
            <TabsTrigger value="resolved">
              Resolved ({alerts.filter(a => a.status === 'resolved').length})
            </TabsTrigger>
            <TabsTrigger value="all">All Alerts</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Alerts List */}
        <div className="space-y-4">
          {filteredAlerts.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">🔔</div>
              <h3 className="text-xl font-semibold mb-2">No {activeTab} alerts</h3>
              <p className="text-gray-600 mb-4">
                {activeTab === 'active' 
                  ? 'There are no active emergency alerts at this time.'
                  : 'Create a new alert or use a quick template to get started.'}
              </p>
              <Button onClick={() => setShowTemplateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Alert from Template
              </Button>
            </Card>
          ) : (
            filteredAlerts.map((alert) => (
              <Card 
                key={alert.alert_id} 
                className={`overflow-hidden ${alert.status === 'active' ? 'border-red-500 border-2 shadow-lg shadow-red-100' : ''}`}
              >
                <div className="flex">
                  {/* Status Bar */}
                  <div className={`w-2 ${STATUS_COLORS[alert.status]}`} />
                  
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{ALERT_TYPE_ICONS[alert.emergency_type]}</span>
                        <div>
                          <h3 className="text-lg font-semibold">{alert.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={PRIORITY_COLORS[alert.priority]}>
                              {alert.priority.toUpperCase()}
                            </Badge>
                            <Badge variant="outline">{alert.emergency_type.replace('_', ' ')}</Badge>
                            <Badge variant="outline" className={STATUS_COLORS[alert.status] + ' text-white'}>
                              {alert.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {alert.status === 'draft' && (
                          <Button 
                            onClick={() => handleSendAlert(alert.alert_id)}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={sending}
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Send Now
                          </Button>
                        )}
                        {alert.status === 'active' && (
                          <Button 
                            onClick={() => handleResolveAlert(alert.alert_id)}
                            variant="outline"
                            className="border-green-500 text-green-600 hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Resolved
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{alert.message}</p>
                    
                    {alert.action_required && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-sm font-medium text-amber-800">
                          Action Required: {alert.action_required}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(alert.created_at).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {alert.target_audience}
                        </span>
                        {alert.affected_area && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {alert.affected_area}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">Channels:</span>
                        {alert.channels?.map(channel => {
                          const Icon = CHANNEL_ICONS[channel];
                          return Icon ? <Icon key={channel} className="w-4 h-4 text-gray-400" /> : null;
                        })}
                      </div>
                    </div>
                    
                    {alert.status === 'active' && alert.sent_count > 0 && (
                      <div className="mt-4 pt-4 border-t flex items-center gap-4 text-sm">
                        <span className="text-green-600">✓ Sent: {alert.sent_count}</span>
                        {alert.failed_count > 0 && (
                          <span className="text-red-600">✗ Failed: {alert.failed_count}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create Alert Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Create Emergency Alert
            </DialogTitle>
            <DialogDescription>
              Create a new emergency alert to notify community members.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Emergency Type</label>
                <Select
                  value={newAlert.emergency_type}
                  onValueChange={(value) => setNewAlert({ ...newAlert, emergency_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ALERT_TYPE_ICONS).map(([type, icon]) => (
                      <SelectItem key={type} value={type}>
                        <span className="flex items-center gap-2">
                          <span>{icon}</span>
                          <span className="capitalize">{type.replace('_', ' ')}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select
                  value={newAlert.priority}
                  onValueChange={(value) => setNewAlert({ ...newAlert, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">🔴 Critical - Immediate danger</SelectItem>
                    <SelectItem value="high">🟠 High - Urgent attention</SelectItem>
                    <SelectItem value="medium">🟡 Medium - Important</SelectItem>
                    <SelectItem value="low">🔵 Low - Informational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Alert Title</label>
              <Input
                value={newAlert.title}
                onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })}
                placeholder="e.g., 🔥 FIRE ALERT - Horse Mountain Ranch"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Message</label>
              <Textarea
                value={newAlert.message}
                onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })}
                placeholder="Provide clear details about the emergency..."
                rows={4}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Action Required (optional)</label>
              <Input
                value={newAlert.action_required}
                onChange={(e) => setNewAlert({ ...newAlert, action_required: e.target.value })}
                placeholder="e.g., Evacuate immediately. Do not delay."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Target Audience</label>
                <Select
                  value={newAlert.target_audience}
                  onValueChange={(value) => setNewAlert({ ...newAlert, target_audience: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Community Members</SelectItem>
                    <SelectItem value="owners">Property Owners Only</SelectItem>
                    <SelectItem value="board">Board Members Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Affected Area (optional)</label>
                <Input
                  value={newAlert.affected_area}
                  onChange={(e) => setNewAlert({ ...newAlert, affected_area: e.target.value })}
                  placeholder="e.g., Upper Ridge, Main Gate"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Delivery Channels</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'sms', label: 'SMS', icon: Smartphone, note: '(Twilio)' },
                  { id: 'email', label: 'Email', icon: Mail, note: '(SendGrid)' },
                  { id: 'push', label: 'Push', icon: Bell, note: '' },
                  { id: 'in_app', label: 'In-App', icon: MessageSquare, note: '' }
                ].map(({ id, label, icon: Icon, note }) => (
                  <Button
                    key={id}
                    type="button"
                    variant={newAlert.channels?.includes(id) ? 'default' : 'outline'}
                    className={newAlert.channels?.includes(id) ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => toggleChannel(id)}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label} {note && <span className="text-xs opacity-70 ml-1">{note}</span>}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Note: SMS and Email delivery requires Twilio and SendGrid integration keys.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAlert}
              className="bg-red-600 hover:bg-red-700"
            >
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quick Alert Templates</DialogTitle>
            <DialogDescription>
              Select a pre-configured template to quickly create an emergency alert.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {templates.map((template) => (
              <Card 
                key={template.id}
                className="cursor-pointer hover:border-red-500 transition-colors"
                onClick={() => applyTemplate(template)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{ALERT_TYPE_ICONS[template.emergency_type]}</span>
                    <div>
                      <h4 className="font-semibold">{template.name}</h4>
                      <Badge className={PRIORITY_COLORS[template.priority]} size="sm">
                        {template.priority}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{template.message}</p>
                  <div className="flex gap-1 mt-2">
                    {template.channels?.map(channel => {
                      const Icon = CHANNEL_ICONS[channel];
                      return Icon ? <Icon key={channel} className="w-3 h-3 text-gray-400" /> : null;
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => { setShowTemplateDialog(false); setShowCreateDialog(true); }}>
              Create Custom Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
  );

  if (embedded) return pageContent;
  return <AdminSidebar>{pageContent}</AdminSidebar>;
};

export default EmergencyAlertsPage;
